import React, { useEffect, useState, useCallback } from 'react';
import { PageProps } from '../models/global';
import { Helmet } from 'react-helmet';
import { hideMenu } from '../utils';
import { useLoading } from '../contexts/LoadingContext';
import { useLanguage } from '../LanguageContext';
import { useTranslate } from '../services/translation';
import { getProducts, PortfolioProduct } from '../api/API';
import ProductList from '../components/ProductList';
import SearchBar from '../components/SearchBar';
import { ProductSkeletonLoader } from '../components/ProductSkeletonLoader';
import { searchTranslationKeys } from '../services/utils';
import translationData from '../translate.json';
import { App } from '../environments/environment';
import '../assets/css/Product.css';

const { PORTFOLIO_USER_ID, LIMIT_PER_PAGE } = App;

interface ProductsState {
  products: PortfolioProduct[];
  total: number;
  hasMore: boolean;
}

const Product = ({ start }: PageProps) => {
  const { translate } = useTranslate();
  const { language } = useLanguage();
  const { startLoading, stopLoading } = useLoading();
  const [state, setState] = useState<ProductsState>({
    products: [],
    total: 0,
    hasMore: false,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const loadProducts = useCallback(
    async (searchTerm: string = '', skipValue: number = 0) => {
      setIsLoading(true);
      startLoading();

      const response = await getProducts(
        PORTFOLIO_USER_ID,
        skipValue,
        LIMIT_PER_PAGE,
        searchTerm
      );

      if (response) {
        setState({
          products: skipValue === 0 ? response.data.data : [...state.products, ...response.data.data],
          total: response.data.count,
          hasMore: (skipValue + LIMIT_PER_PAGE) < response.data.count,
        });
      }

      setIsLoading(false);
      stopLoading();
    },
    [startLoading, stopLoading, state.products]
  );

  useEffect(() => {
    start!(1);
    hideMenu();
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      setSkip(0);
      const translationKeys = searchTranslationKeys(query, language, translationData);
      const searchTerm = translationKeys || query;
      loadProducts(searchTerm, 0);
    },
    [loadProducts, language]
  );

  const handleLoadMore = useCallback(() => {
    const newSkip = skip + LIMIT_PER_PAGE;
    setSkip(newSkip);
    const translationKeys = searchTranslationKeys(searchQuery, language, translationData);
    const searchTerm = translationKeys || searchQuery;
    loadProducts(searchTerm, newSkip);
  }, [skip, searchQuery, loadProducts, language]);

  return (
    <div className="product-page width-100p">
      <Helmet>
        <meta charSet="utf-8" name="description" content={translate('portfolio.gence.product_page.desc')} />
        <title>{translate('portfolio.gence.product_page.title')}</title>
        <link rel="canonical" href={`https://gence.bemanjary.com/${language}/products`} />
        <meta property="og:title" content={translate('portfolio.gence.product_page.title')} />
        <meta property="og:description" content={translate('portfolio.gence.product_page.desc')} />
        <meta property="og:type" content="website" />
        <link rel="alternate" hrefLang="fr" href="https://gence.bemanjary.com/fr/products" />
        <link rel="alternate" hrefLang="en" href="https://gence.bemanjary.com/en/products" />
        <link rel="alternate" hrefLang="mg" href="https://gence.bemanjary.com/mg/products" />
        <link rel="alternate" hrefLang="x-default" href="https://gence.bemanjary.com/fr/products" />
      </Helmet>

      <div className="product-page-header">
        <h1 className="product-page-title white">{translate('common.my_projects')}</h1>
        <p className="product-page-subtitle light">
          {state.total} {state.total > 1 ? translate('common.projects') : translate("common.project")}
        </p>
      </div>

      <SearchBar
        onSearch={handleSearch}
        placeholder={translate('common.search_projects')}
      />

      <div className="product-page-content">
        {isLoading && state.products.length === 0 ? (
          <ProductSkeletonLoader count={LIMIT_PER_PAGE} />
        ) : state.products.length > 0 ? (
          <>
            <ProductList products={state.products} />
            {state.hasMore && (
              <div className="product-load-more">
                <button
                  className="btn-load-more"
                  onClick={handleLoadMore}
                  disabled={isLoading}
                >
                  {isLoading ? translate("common.loading") : translate("common.load_more")}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="product-page-empty">
            <p className="light">{translate("common.no_projects_found")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Product;